import React, { useState, useEffect } from "react";
import { Routes, Route, useLocation, useMatch, useParams } from "react-router-dom";
import { Portal } from "@chakra-ui/portal";
import Navbar from "./Navbar";
import Footer from "components/footer/Footer";
import routes from "routes";
import DealOverview from "pages/overview/index.jsx";
import PortfolioOverview from "pages/portfolio/index.jsx";
import DealTracker from "pages/deal-tracker/index.jsx";
import ManageGroups from 'pages/overview/groups/ManageGroups';
import Photos from "pages/photos/index.jsx";

import FinanceChartPage from "pages/overview/finances/FinanceChartPage.jsx";
import FinancialModelPage from "pages/overview/finances/FinancialModelPage.jsx";
import CompetitionChartPage from "pages/overview/competition/CompetitionChartPage.jsx";
import { useTabs } from "contexts/TabContext";
import { useDealData } from "contexts/DealContext";
import { usePortfolioData } from "contexts/PortfolioContext";

const AdminLayout = ({ pageToShow }) => {
  const location = useLocation();
  const [currentRoute, setCurrentRoute] = useState("Main Dashboard");
  const manageGroupsMatch = useMatch('/manage-groups');

  // Match both old and new finance chart URL patterns
  const financeChartMatch = useMatch('/finance-charts/:chartType');
  const dealFinanceChartMatch = useMatch('/deals/:dealName/finance-charts/:chartType');
  const portfolioFinanceChartMatch = useMatch('/portfolio/:portfolioName/finance-charts/:chartType');
  const portfolioFacilityFinanceChartMatch = useMatch('/portfolio/:portfolioName/:facilityName/finance-charts/:chartType');

  // Match both old and new competition chart URL patterns
  const competitionChartMatch = useMatch('/competition-charts/:chartType');
  const dealCompetitionChartMatch = useMatch('/deals/:dealName/competition-charts/:chartType');
  const portfolioCompetitionChartMatch = useMatch('/portfolio/:portfolioName/competition-charts/:chartType');
  const portfolioFacilityCompetitionChartMatch = useMatch('/portfolio/:portfolioName/:facilityName/competition-charts/:chartType');

  // Match financial model URL patterns
  const financialModelMatch = useMatch('/financial-model');
  const dealFinancialModelMatch = useMatch('/deals/:dealName/financial-model');
  const portfolioFinancialModelMatch = useMatch('/portfolio/:portfolioName/financial-model');
  const portfolioFacilityFinancialModelMatch = useMatch('/portfolio/:portfolioName/:facilityName/financial-model');

  const dealMatch = useMatch('/deals/:dealName');
  const portfolioMatch = useMatch('/portfolio/:portfolioName');
  const portfolioFacilityMatch = useMatch('/portfolio/:portfolioName/:facilityName');

  const { maintainingTab } = useTabs();
  const { isDealLoading, selectDeal, currentDealName } = useDealData();
  const {
    isPortfolioLoading,
    selectPortfolio,
    selectPortfolioFacility,
    currentPortfolioName,
    currentPortfolioFacilityName,
    selectedPeriod: portfolioSelectedPeriod
  } = usePortfolioData();

  // Extract deal/portfolio name from URL for finance chart, competition chart, and regular routes
  useEffect(() => {
    // Handle deal name extraction from financial model URLs
    if (dealFinancialModelMatch && dealFinancialModelMatch.params.dealName) {
      const dealNameFromUrl = decodeURIComponent(dealFinancialModelMatch.params.dealName);
      if (dealNameFromUrl && dealNameFromUrl !== currentDealName && !isDealLoading) {
        selectDeal(dealNameFromUrl);
      }
    }
    // Handle portfolio name extraction from financial model URLs
    else if (portfolioFinancialModelMatch && portfolioFinancialModelMatch.params.portfolioName) {
      const portfolioNameFromUrl = decodeURIComponent(portfolioFinancialModelMatch.params.portfolioName);
      const portfolioNameLower = portfolioNameFromUrl.toLowerCase();
      const currentPortfolioNameLower = currentPortfolioName?.toLowerCase();
      if (portfolioNameFromUrl && portfolioNameLower !== currentPortfolioNameLower && !isPortfolioLoading) {
        selectPortfolio(portfolioNameLower, portfolioSelectedPeriod);
      }
    }
    // Handle portfolio facility name extraction from financial model URLs
    else if (portfolioFacilityFinancialModelMatch && portfolioFacilityFinancialModelMatch.params.portfolioName && portfolioFacilityFinancialModelMatch.params.facilityName) {
      const portfolioNameFromUrl = decodeURIComponent(portfolioFacilityFinancialModelMatch.params.portfolioName);
      const facilityNameFromUrl = decodeURIComponent(portfolioFacilityFinancialModelMatch.params.facilityName);
      const portfolioNameLower = portfolioNameFromUrl.toLowerCase();
      const currentPortfolioNameLower = currentPortfolioName?.toLowerCase();
      const portfolioChanged = portfolioNameLower !== currentPortfolioNameLower;
      const facilityChanged = facilityNameFromUrl && facilityNameFromUrl !== currentPortfolioFacilityName;
      if (portfolioChanged && !isPortfolioLoading) {
        selectPortfolio(portfolioNameLower, portfolioSelectedPeriod).then(() => {
          if (facilityChanged && !isPortfolioLoading) {
            selectPortfolioFacility(facilityNameFromUrl, portfolioSelectedPeriod);
          }
        });
      } else if (facilityChanged && !isPortfolioLoading) {
        selectPortfolioFacility(facilityNameFromUrl, portfolioSelectedPeriod);
      }
    }
    // Handle deal name extraction from finance chart URLs
    else if (dealFinanceChartMatch && dealFinanceChartMatch.params.dealName) {
      const dealNameFromUrl = decodeURIComponent(dealFinanceChartMatch.params.dealName);

      // Only trigger deal selection if it's different from current deal
      if (dealNameFromUrl && dealNameFromUrl !== currentDealName && !isDealLoading) {
        selectDeal(dealNameFromUrl);
      }
    }
    // Handle deal name extraction from competition chart URLs
    else if (dealCompetitionChartMatch && dealCompetitionChartMatch.params.dealName) {
      const dealNameFromUrl = decodeURIComponent(dealCompetitionChartMatch.params.dealName);

      // Only trigger deal selection if it's different from current deal
      if (dealNameFromUrl && dealNameFromUrl !== currentDealName && !isDealLoading) {
        selectDeal(dealNameFromUrl);
      }
    }
    // Handle deal name extraction from regular deal URLs
    else if (dealMatch && dealMatch.params.dealName) {
      const dealNameFromUrl = decodeURIComponent(dealMatch.params.dealName);

      // Only trigger deal selection if it's different from current deal
      if (dealNameFromUrl && dealNameFromUrl !== currentDealName && !isDealLoading) {
        selectDeal(dealNameFromUrl);
      }
    }
    // Handle portfolio name extraction from finance chart URLs
    else if (portfolioFinanceChartMatch && portfolioFinanceChartMatch.params.portfolioName) {
      const portfolioNameFromUrl = decodeURIComponent(portfolioFinanceChartMatch.params.portfolioName);
      const portfolioNameLower = portfolioNameFromUrl.toLowerCase();
      const currentPortfolioNameLower = currentPortfolioName?.toLowerCase();

      // Only trigger portfolio selection if it's different from current portfolio
      if (portfolioNameFromUrl && portfolioNameLower !== currentPortfolioNameLower && !isPortfolioLoading) {
        selectPortfolio(portfolioNameLower, portfolioSelectedPeriod);
      }
    }
    // Handle portfolio name extraction from competition chart URLs
    else if (portfolioCompetitionChartMatch && portfolioCompetitionChartMatch.params.portfolioName) {
      const portfolioNameFromUrl = decodeURIComponent(portfolioCompetitionChartMatch.params.portfolioName);
      const portfolioNameLower = portfolioNameFromUrl.toLowerCase();
      const currentPortfolioNameLower = currentPortfolioName?.toLowerCase();

      // Only trigger portfolio selection if it's different from current portfolio
      if (portfolioNameFromUrl && portfolioNameLower !== currentPortfolioNameLower && !isPortfolioLoading) {
        selectPortfolio(portfolioNameLower, portfolioSelectedPeriod);
      }
    }
    // Handle portfolio facility name extraction from finance chart URLs
    else if (portfolioFacilityFinanceChartMatch && portfolioFacilityFinanceChartMatch.params.portfolioName && portfolioFacilityFinanceChartMatch.params.facilityName) {
      const portfolioNameFromUrl = decodeURIComponent(portfolioFacilityFinanceChartMatch.params.portfolioName);
      const facilityNameFromUrl = decodeURIComponent(portfolioFacilityFinanceChartMatch.params.facilityName);
      const portfolioNameLower = portfolioNameFromUrl.toLowerCase();
      const currentPortfolioNameLower = currentPortfolioName?.toLowerCase();
      const portfolioChanged = portfolioNameLower !== currentPortfolioNameLower;
      const facilityChanged = facilityNameFromUrl && facilityNameFromUrl !== currentPortfolioFacilityName;

      // First ensure the correct portfolio is selected
      if (portfolioChanged && !isPortfolioLoading) {
        selectPortfolio(portfolioNameLower, portfolioSelectedPeriod).then(() => {
          if (facilityChanged && !isPortfolioLoading) {
            selectPortfolioFacility(facilityNameFromUrl, portfolioSelectedPeriod);
          }
        });
      } else if (facilityChanged && !isPortfolioLoading) {
        selectPortfolioFacility(facilityNameFromUrl, portfolioSelectedPeriod);
      }
    }
    // Handle portfolio facility name extraction from competition chart URLs
    else if (portfolioFacilityCompetitionChartMatch && portfolioFacilityCompetitionChartMatch.params.portfolioName && portfolioFacilityCompetitionChartMatch.params.facilityName) {
      const portfolioNameFromUrl = decodeURIComponent(portfolioFacilityCompetitionChartMatch.params.portfolioName);
      const facilityNameFromUrl = decodeURIComponent(portfolioFacilityCompetitionChartMatch.params.facilityName);
      const portfolioNameLower = portfolioNameFromUrl.toLowerCase();
      const currentPortfolioNameLower = currentPortfolioName?.toLowerCase();
      const portfolioChanged = portfolioNameLower !== currentPortfolioNameLower;
      const facilityChanged = facilityNameFromUrl && facilityNameFromUrl !== currentPortfolioFacilityName;

      // First ensure the correct portfolio is selected
      if (portfolioChanged && !isPortfolioLoading) {
        selectPortfolio(portfolioNameLower, portfolioSelectedPeriod).then(() => {
          if (facilityChanged && !isPortfolioLoading) {
            selectPortfolioFacility(facilityNameFromUrl, portfolioSelectedPeriod);
          }
        });
      } else if (facilityChanged && !isPortfolioLoading) {
        selectPortfolioFacility(facilityNameFromUrl, portfolioSelectedPeriod);
      }
    }
    // Handle portfolio name extraction from regular portfolio URLs
    // BUT only if there's NO facility in the URL (otherwise let the facility handler deal with it)
    else if (portfolioMatch && portfolioMatch.params.portfolioName && !portfolioFacilityMatch) {
      const portfolioNameFromUrl = decodeURIComponent(portfolioMatch.params.portfolioName);

      // Compare lowercase versions since currentPortfolioName is sentence case but URL is lowercase
      const portfolioNameLower = portfolioNameFromUrl.toLowerCase();
      const currentPortfolioNameLower = currentPortfolioName?.toLowerCase();

      // Only trigger portfolio selection if it's different from current portfolio
      if (portfolioNameFromUrl && portfolioNameLower !== currentPortfolioNameLower && !isPortfolioLoading) {
        selectPortfolio(portfolioNameLower, portfolioSelectedPeriod);
      }
    }
    // Handle portfolio facility name extraction from regular portfolio URLs
    else if (portfolioFacilityMatch && portfolioFacilityMatch.params.portfolioName && portfolioFacilityMatch.params.facilityName) {
      const portfolioNameFromUrl = decodeURIComponent(portfolioFacilityMatch.params.portfolioName);
      const facilityNameFromUrl = decodeURIComponent(portfolioFacilityMatch.params.facilityName);

      // Compare lowercase versions since currentPortfolioName is sentence case but URL might be lowercase
      const portfolioNameLower = portfolioNameFromUrl.toLowerCase();
      const currentPortfolioNameLower = currentPortfolioName?.toLowerCase();
      const portfolioChanged = portfolioNameLower !== currentPortfolioNameLower;
      const facilityChanged = facilityNameFromUrl && facilityNameFromUrl !== currentPortfolioFacilityName;

      // First ensure the correct portfolio is selected
      if (portfolioChanged && !isPortfolioLoading) {
        selectPortfolio(portfolioNameLower, portfolioSelectedPeriod).then(() => {
          // After portfolio is selected, select the facility
          if (facilityNameFromUrl && facilityChanged && !isPortfolioLoading) {
            selectPortfolioFacility(facilityNameFromUrl, portfolioSelectedPeriod);
          }
        });
      } else if (facilityChanged && !isPortfolioLoading) {
        // Portfolio is already correct, just select the facility
        selectPortfolioFacility(facilityNameFromUrl, portfolioSelectedPeriod);
      }
    }
  }, [
    dealFinancialModelMatch,
    portfolioFinancialModelMatch,
    portfolioFacilityFinancialModelMatch,
    dealFinanceChartMatch,
    dealCompetitionChartMatch,
    dealMatch,
    portfolioFinanceChartMatch,
    portfolioCompetitionChartMatch,
    portfolioMatch,
    portfolioFacilityFinanceChartMatch,
    portfolioFacilityCompetitionChartMatch,
    portfolioFacilityMatch,
    selectDeal,
    selectPortfolio,
    selectPortfolioFacility,
    currentDealName,
    currentPortfolioName,
    currentPortfolioFacilityName,
    portfolioSelectedPeriod,
    isDealLoading,
    isPortfolioLoading
  ]);

  useEffect(() => {
    getActiveRoute(routes);
    // eslint-disable-next-line
  }, [location.pathname]);

  // functions for changing the states from components
  const getActiveRoute = (routes) => {
    let activeRoute = "Default Brand Text";
    for (let i = 0; i < routes.length; i++) {
      if (routes[i].collapse) {
        let collapseActiveRoute = getActiveRoute(routes[i].items);
        if (collapseActiveRoute !== activeRoute) {
          return collapseActiveRoute;
        }
      } else {
        if (
          window.location.href.indexOf(routes[i].layout + routes[i].path) !== -1
        ) {
          setCurrentRoute(routes[i].name);
        }
      }
    }
    return activeRoute;
  };

  // Determine which chart page to show based on current path
  const getCurrentChartPage = () => {
    const path = location.pathname;
    if (path.includes('/finance-charts/')) {
      return <FinanceChartPage />;
    } else if (path.includes('/competition-charts/')) {
      return <CompetitionChartPage />;
    }
    return <FinanceChartPage />; // default fallback
  };

  // Helper function to get conditional padding classes
  const getConditionalPadding = () => {
    // On small screens the navbar's control row stacks vertically (portfolio +
    // period + facility selectors), so we need extra top padding to clear it.
    // Larger screens keep the controls inline and use the base pt-24.
    return "pt-48 sm:pt-24";
  };

  // If we're maintaining a tab during deal change, continue showing the appropriate chart
  if (maintainingTab && isDealLoading) {
    return (
      <div className="flex h-full w-full bg-background-100 dark:bg-background-900">
        <div className="h-full w-full font-dm dark:bg-background-900">
          <main className='w-full'>
            <Portal>
              <Navbar />
            </Portal>
            <div className={`mx-auto min-h-screen p-3 ${getConditionalPadding()}`}>
              {getCurrentChartPage()}
            </div>
            <div className="p-3">
              <Footer />
            </div>
          </main>
        </div>
      </div>
    );
  }

  // Render ManageGroups directly if the path is /manage-groups
  if (manageGroupsMatch) {
    return (
      <div className="flex h-full w-full bg-background-100 dark:bg-background-900">
        <div className="h-full w-full font-dm dark:bg-background-900">
          <main className='w-full'>
            <Portal>
              <Navbar />
            </Portal>
            <div className={`mx-auto min-h-screen p-3 ${getConditionalPadding()}`}>
              <ManageGroups />
            </div>
            <div className="p-3">
              <Footer />
            </div>
          </main>
        </div>
      </div>
    );
  }

  // Directly render FinanceChartPage if the path is finance-charts
  if (financeChartMatch || dealFinanceChartMatch || portfolioFinanceChartMatch || portfolioFacilityFinanceChartMatch || pageToShow === "FinanceChart") {
    return (
      <div className="flex h-full w-full bg-background-100 dark:bg-background-900">
        <div className="h-full w-full font-dm dark:bg-background-900">
          <main className='w-full'>
            <Portal>
              <Navbar />
            </Portal>
            <div className={`mx-auto min-h-screen p-3 ${getConditionalPadding()}`}>
              <FinanceChartPage />
            </div>
            <div className="p-3">
              <Footer />
            </div>
          </main>
        </div>
      </div>
    );
  }

  // Directly render CompetitionChartPage if the path is competition-charts
  if (competitionChartMatch || dealCompetitionChartMatch || portfolioCompetitionChartMatch || portfolioFacilityCompetitionChartMatch || pageToShow === "CompetitionChart") {
    return (
      <div className="flex h-full w-full bg-background-100 dark:bg-background-900">
        <div className="h-full w-full font-dm dark:bg-background-900">
          <main className='w-full'>
            <Portal>
              <Navbar />
            </Portal>
            <div className={`mx-auto min-h-screen p-3 ${getConditionalPadding()}`}>
              <CompetitionChartPage />
            </div>
            <div className="p-3">
              <Footer />
            </div>
          </main>
        </div>
      </div>
    );
  }

  // Directly render FinancialModelPage if the path is financial-model
  if (financialModelMatch || dealFinancialModelMatch || portfolioFinancialModelMatch || portfolioFacilityFinancialModelMatch || pageToShow === "FinancialModel") {
    return (
      <div className="flex h-full w-full bg-background-100 dark:bg-background-900">
        <div className="h-full w-full font-dm dark:bg-background-900">
          <main className='w-full'>
            <Portal>
              <Navbar />
            </Portal>
            <div className={`mx-auto min-h-screen p-3 ${getConditionalPadding()}`}>
              <FinancialModelPage />
            </div>
            <div className="p-3">
              <Footer />
            </div>
          </main>
        </div>
      </div>
    );
  }

  // Directly render PortfolioOverview if specified or on portfolio routes
  if (pageToShow === "PortfolioOverview") {
    return (
      <div className="flex h-full w-full bg-background-100 dark:bg-background-900">
        <div className="h-full w-full font-dm dark:bg-background-900">
          <main className='w-full'>
            <Portal>
              <Navbar />
            </Portal>
            <div className={`mx-auto min-h-screen p-3 ${getConditionalPadding()}`}>
              <PortfolioOverview />
            </div>
            <div className="p-3">
              <Footer />
            </div>
          </main>
        </div>
      </div>
    );
  }

  // Directly render specific components based on pageToShow prop from App.jsx
  if (pageToShow === "DealTracker") {
    return (
      <div className="flex h-full w-full bg-background-100 dark:bg-background-900">
        <div className="h-full w-full font-dm dark:bg-background-900">
          <main className='w-full'>
            <Portal>
              <Navbar />
            </Portal>
            <div className={`mx-auto min-h-screen p-3 ${getConditionalPadding()}`}>
              <DealTracker />
            </div>
            <div className="p-3">
              <Footer />
            </div>
          </main>
        </div>
      </div>
    );
  }

  // Default layout with routes for other pages
  return (
    <div className="flex h-full w-full bg-background-100 dark:bg-background-900">
      <div className="h-full w-full font-dm dark:bg-background-900">
        <main className='w-full'>
          <Portal>
            <Navbar />
          </Portal>
          <div className={`mx-auto min-h-screen p-3 ${getConditionalPadding()}`}>
            <Routes>
              <Route path="/" element={<DealOverview />} />
              <Route path="/deals" element={<DealOverview />} />
              <Route path="/deals/:dealName" element={<DealOverview />} />
              <Route path="/portfolio" element={<PortfolioOverview />} />
              <Route path="/portfolio/:portfolioName" element={<PortfolioOverview />} />
              <Route path="/portfolio/:portfolioName/:facilityName" element={<PortfolioOverview />} />
              <Route path="/deal-tracker" element={<DealTracker />} />
              <Route path="/photos" element={<Photos />} />
              <Route path="/status" element={<DealTracker />} />
            </Routes>
          </div>
          <div className="p-3">
            <Footer />
          </div>
        </main>
      </div>
    </div>
  );
}

export default AdminLayout;
